/**
 * SelectAllMultiSelect – Unified multi-select dropdown with fixed Select All / Clear All header.
 *
 * STANDARD UI VALUES (baked in – do NOT override per-page):
 *   fontSize:     0.875rem  (input text + label)
 *   itemFontSize: 0.875rem  (menu items)
 *   height:       MUI size="small" → 40px
 *   maxHeight:    300px (dropdown menu)
 *   scroll:       always starts from top (variant='menu' + anchorOrigin bottom-left)
 *   colors:       SEARCE_COLORS from theme
 */

import React, { useRef, useMemo, useReducer, useCallback } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Typography,
  Button,
  TextField,
  Skeleton,
  alpha,
  ListSubheader,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { SelectAll as SelectAllIcon } from '@mui/icons-material';
import { SEARCE_COLORS } from '../../theme';
import { FILTER_UI_STANDARD } from '../../config/filterUiStandard';
import { InlineSpinner } from './loading';

// ============================================================================
// STANDARD UI CONSTANTS — single source of truth for ALL filter dropdowns
// ============================================================================

const STANDARD = {
  fontSize: FILTER_UI_STANDARD.controlFontSize,
  itemFontSize: FILTER_UI_STANDARD.itemFontSize,
  maxHeight: FILTER_UI_STANDARD.menuMaxHeight,
  maxMenuWidth: FILTER_UI_STANDARD.menuMaxWidth,
  checkboxSize: '1rem',
  selectAllFontSize: FILTER_UI_STANDARD.buttonFontSize,
  headerBg: '#fafafa',
} as const;

/** Stable reference so TextField does not reset internal state on parent re-renders. */
const SEARCH_TEXT_FIELD_INPUT_PROPS = { sx: { fontSize: STANDARD.fontSize } } as const;

// ============================================================================
// TYPES
// ============================================================================

export interface SelectAllOption {
  value: string;
  label: string;
  group?: string;
  color?: string;
}

export interface SelectAllOptionGroup {
  key: string;
  label: string;
  muted?: boolean;
  indent?: boolean;
}

/** Section titles + items in order (avoids fragile group-key filtering with MUI Select multiple). */
export interface SelectAllFlatSection {
  header: string;
  muted?: boolean;
  /** Indent row checkboxes/labels (e.g. optional bucket). */
  indentItems?: boolean;
  items: SelectAllOption[];
}

export interface SelectAllMultiSelectProps {
  label: string;
  options: SelectAllOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;

  /** Show Select All / Clear All header. Default: true */
  enableSelectAll?: boolean;
  /** Show search field in dropdown header. Default: false */
  enableSearch?: boolean;
  searchPlaceholder?: string;

  /** Show loading skeleton instead of the select */
  loading?: boolean;
  /** Show spinner inside dropdown with a message */
  loadingMessage?: string;
  disabled?: boolean;
  emptyMessage?: string;

  /** Text shown when nothing is selected or all are selected. Default: "" */
  allLabel?: string;
  /** Custom renderValue override — only use for complex loading/Skeleton states */
  renderValue?: (selected: string[]) => React.ReactNode;

  groups?: SelectAllOptionGroup[];
  selectAllGroupKeys?: string[];
  /**
   * When provided, "Select All" sets exactly these values (e.g. assigned-only products in a flat list).
   * Omit to use group-based or all-options behavior.
   */
  selectAllValues?: string[];
  /**
   * Renders ListSubheader + MenuItems per section in order. Prefer over `groups` when section titles are needed
   * with reliable multi-select (DRI Products). `options` should match `flatSections.flatMap(s => s.items)`.
   */
  flatSections?: SelectAllFlatSection[];

  maxHeight?: number;
  menuWidth?: number | 'auto';

  sx?: SxProps<Theme>;
  menuPaperSx?: SxProps<Theme>;
  fullWidth?: boolean;
  /** Wrap long item text instead of expanding dropdown. Default: true */
  wrapItemText?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

const SelectAllMultiSelect: React.FC<SelectAllMultiSelectProps> = ({
  label,
  options,
  selectedValues,
  onChange,
  enableSelectAll = true,
  enableSearch = false,
  searchPlaceholder,
  loading = false,
  loadingMessage,
  disabled = false,
  emptyMessage = 'No options available',
  allLabel = '',
  renderValue: renderValueProp,
  groups,
  selectAllGroupKeys,
  selectAllValues,
  flatSections,
  maxHeight = STANDARD.maxHeight,
  menuWidth = 'auto',
  sx,
  menuPaperSx,
  fullWidth = true,
  wrapItemText = true,
}) => {
  /** Live query for filtering; DOM input stays uncontrolled so typing is not interrupted by Select/Menu re-renders. */
  const searchQueryRef = useRef('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const [, bumpSearchFilter] = useReducer((n: number) => n + 1, 0);

  const getFilteredOptions = () => {
    if (!enableSearch || !searchQueryRef.current.trim()) return options;
    const q = searchQueryRef.current.toLowerCase().trim();
    return options.filter((o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q));
  };

  const getFilteredFlatSections = (): SelectAllFlatSection[] => {
    if (!flatSections?.length) return [];
    if (!enableSearch || !searchQueryRef.current.trim()) return flatSections;
    const q = searchQueryRef.current.toLowerCase().trim();
    return flatSections
      .map((sec) => ({
        ...sec,
        items: sec.items.filter(
          (o) => o.label.toLowerCase().includes(q) || o.value.toLowerCase().includes(q)
        ),
      }))
      .filter((sec) => sec.items.length > 0);
  };

  const selectAllRef = useRef(() => {});
  const clearAllRef = useRef(() => {});

  selectAllRef.current = () => {
    if (selectAllValues !== undefined) {
      onChange([...selectAllValues]);
      return;
    }
    if (selectAllGroupKeys && selectAllGroupKeys.length > 0) {
      const groupValues = options
        .filter((o) => o.group && selectAllGroupKeys.includes(o.group))
        .map((o) => o.value);
      onChange(groupValues);
    } else if (enableSearch && searchQueryRef.current.trim()) {
      const filtered = getFilteredOptions();
      onChange(filtered.map((o) => o.value));
    } else {
      onChange(options.map((o) => o.value));
    }
  };

  clearAllRef.current = () => onChange([]);

  /** Reset search after the menu closes. Do not pass this as Menu `onClose` — it would replace Select's handler and leave the menu stuck open (backdrop blocks the page). */
  const resetSearchAfterMenuExit = useCallback(() => {
    searchQueryRef.current = '';
    const el = searchInputRef.current;
    if (el) el.value = '';
    bumpSearchFilter();
  }, []);

  const CustomPaper = useMemo(
    () =>
      React.forwardRef<HTMLDivElement, any>(function CustomPaper(
        { children, style, ...other },
        ref
      ) {
        return (
          <div
            ref={ref}
            {...other}
            style={{ ...style, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
          >
            <Box sx={{ flexShrink: 0 }}>
              {enableSearch && (
                <Box sx={{ p: 1, borderBottom: 1, borderColor: 'divider' }}>
                  <TextField
                    size="small"
                    placeholder={searchPlaceholder || `Search ${label.toLowerCase()}...`}
                    defaultValue=""
                    inputRef={searchInputRef}
                    onChange={(e) => {
                      searchQueryRef.current = e.target.value;
                      bumpSearchFilter();
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    onKeyDown={(e) => e.stopPropagation()}
                    sx={{ width: '100%' }}
                    InputProps={SEARCH_TEXT_FIELD_INPUT_PROPS}
                    autoComplete="off"
                  />
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 1,
                  py: 0.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: STANDARD.headerBg,
                }}
              >
                <Button
                  size="small"
                  startIcon={<SelectAllIcon sx={{ fontSize: '0.875rem !important' }} />}
                  onClick={(e) => { e.stopPropagation(); selectAllRef.current(); }}
                  sx={{ fontSize: STANDARD.selectAllFontSize, minWidth: 'auto', textTransform: 'none', whiteSpace: 'nowrap' }}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  onClick={(e) => { e.stopPropagation(); clearAllRef.current(); }}
                  sx={{ fontSize: STANDARD.selectAllFontSize, minWidth: 'auto', textTransform: 'none', whiteSpace: 'nowrap', color: SEARCE_COLORS.textSecondary }}
                >
                  Clear All
                </Button>
              </Box>
            </Box>
            <div style={{ overflow: 'auto', flex: 1 }}>{children}</div>
          </div>
        );
      }),
    [enableSearch]
  );

  const defaultRenderValue = useCallback(
    (selected: string[]) => {
      let text: string;
      if (selected.length === 0) {
        text = allLabel || '';
      } else if (selected.length === options.length && options.length > 0) {
        text = allLabel || `${selected.length} selected`;
      } else {
        text = `${selected.length} selected`;
      }

      return (
        <Typography
          sx={{
            fontSize: STANDARD.fontSize,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%',
            color: selected.length === 0 && !allLabel ? 'text.secondary' : undefined,
          }}
          title={text}
        >
          {text}
        </Typography>
      );
    },
    [allLabel, options.length]
  );

  const selectOutlinedInput = useMemo(
    () => <OutlinedInput label={label} sx={{ fontSize: STANDARD.fontSize }} />,
    [label]
  );

  const hasOptions =
    flatSections?.length && flatSections.some((s) => s.items.length > 0)
      ? true
      : options.length > 0;
  const showCustomPaper = enableSelectAll && hasOptions;

  /** Stable identity while typing in search — new inline MenuProps each render remounts the paper and drops focus. */
  const selectMenuProps = useMemo(
    () => ({
      variant: 'menu' as const,
      disableAutoFocus: true,
      disableEnforceFocus: true,
      TransitionProps: { onExited: resetSearchAfterMenuExit },
      PaperProps: {
        ...(showCustomPaper ? { component: CustomPaper } : {}),
        style: { maxHeight, width: menuWidth, maxWidth: STANDARD.maxMenuWidth },
        ...(menuPaperSx ? { sx: menuPaperSx } : {}),
      },
      anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
      transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
      MenuListProps: { sx: { pt: 0 }, autoFocus: false, autoFocusItem: false },
    }),
    [showCustomPaper, CustomPaper, maxHeight, menuWidth, menuPaperSx, resetSearchAfterMenuExit]
  );

  if (loading) {
    return (
      <FormControl fullWidth={fullWidth} size="small" sx={sx}>
        <Skeleton variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
      </FormControl>
    );
  }

  const filteredOptions = flatSections?.length ? options : getFilteredOptions();

  const renderMenuItems = () => {
    if (loadingMessage) {
      return (
        <MenuItem disabled>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ mr: 1, display: 'flex' }}>
              <InlineSpinner size={16} thickness={5} />
            </Box>
            <Typography sx={{ fontSize: STANDARD.fontSize }}>{loadingMessage}</Typography>
          </Box>
        </MenuItem>
      );
    }

    if (flatSections?.length) {
      const sectionsToRender = getFilteredFlatSections();
      const total = sectionsToRender.reduce((n, s) => n + s.items.length, 0);
      if (total === 0) {
        return (
          <MenuItem disabled>
            <Typography sx={{ fontSize: STANDARD.fontSize, fontStyle: 'italic', color: 'text.secondary' }}>
              {emptyMessage}
            </Typography>
          </MenuItem>
        );
      }
      return sectionsToRender.map((section, si) => (
        <React.Fragment key={`flat-sec-${si}-${section.header}`}>
          <ListSubheader
            disableSticky
            sx={{
              backgroundColor: section.muted
                ? alpha(SEARCE_COLORS.neutral_100, 0.05)
                : alpha(SEARCE_COLORS.primary, 0.08),
              fontWeight: 600,
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              lineHeight: '32px',
              py: 0,
              color: section.muted ? SEARCE_COLORS.neutral_600 : SEARCE_COLORS.primary,
              ...(section.indentItems ? { pl: 3 } : {}),
            }}
          >
            {section.header}
          </ListSubheader>
          {section.items.map((opt) => (
            <MenuItem
              key={opt.value}
              value={opt.value}
              sx={{ py: 0.5, alignItems: 'center', ...(section.indentItems ? { pl: 3 } : {}) }}
            >
              <Checkbox
                checked={selectedValues.includes(opt.value)}
                size="small"
                tabIndex={-1}
                sx={{
                  mr: 0.5,
                  p: '4px',
                  pointerEvents: 'none',
                  '& .MuiSvgIcon-root': { fontSize: STANDARD.checkboxSize },
                }}
              />
              <ListItemText
                primary={opt.label}
                primaryTypographyProps={{
                  sx: {
                    fontSize: STANDARD.itemFontSize,
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    lineHeight: 1.3,
                    ...(section.muted ? { color: alpha(SEARCE_COLORS.neutral_900, 0.7) } : {}),
                  },
                }}
              />
            </MenuItem>
          ))}
        </React.Fragment>
      ));
    }

    if (filteredOptions.length === 0) {
      return (
        <MenuItem disabled>
          <Typography sx={{ fontSize: STANDARD.fontSize, fontStyle: 'italic', color: 'text.secondary' }}>
            {emptyMessage}
          </Typography>
        </MenuItem>
      );
    }

    if (groups && groups.length > 0) {
      return groups.map((group) => {
        const groupOptions = filteredOptions.filter((o) => o.group === group.key);
        if (groupOptions.length === 0) return null;
        return (
          <React.Fragment key={group.key}>
            {/* ListSubheader keeps group labels out of Select's selectable MenuItem list (disabled MenuItem headers broke multi-select for following groups). */}
            <ListSubheader
              disableSticky
              sx={{
                backgroundColor: group.muted
                  ? alpha(SEARCE_COLORS.neutral_100, 0.05)
                  : alpha(SEARCE_COLORS.primary, 0.08),
                fontWeight: 600,
                fontSize: '0.75rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                lineHeight: '32px',
                py: 0,
                color: group.muted ? SEARCE_COLORS.neutral_600 : SEARCE_COLORS.primary,
                ...(group.indent ? { pl: 3 } : {}),
              }}
            >
              {group.label}
            </ListSubheader>
            {groupOptions.map((opt) => (
              <MenuItem
                key={opt.value}
                value={opt.value}
                sx={{ py: 0.5, alignItems: 'center', ...(group.indent ? { pl: 3 } : {}) }}
              >
                <Checkbox
                  checked={selectedValues.includes(opt.value)}
                  size="small"
                  tabIndex={-1}
                  sx={{
                    mr: 0.5,
                    p: '4px',
                    pointerEvents: 'none',
                    '& .MuiSvgIcon-root': { fontSize: STANDARD.checkboxSize },
                  }}
                />
                <ListItemText
                  primary={opt.label}
                  primaryTypographyProps={{
                    sx: {
                      fontSize: STANDARD.itemFontSize,
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                      lineHeight: 1.3,
                      ...(group.muted ? { color: alpha(SEARCE_COLORS.neutral_900, 0.7) } : {}),
                    },
                  }}
                />
              </MenuItem>
            ))}
          </React.Fragment>
        );
      });
    }

    return filteredOptions.map((opt) => (
      <MenuItem key={opt.value} value={opt.value} sx={{ py: 0.5, alignItems: 'center' }}>
        <Checkbox
          checked={selectedValues.includes(opt.value)}
          size="small"
          tabIndex={-1}
          sx={{
            mr: 0.5,
            p: '4px',
            pointerEvents: 'none',
            '& .MuiSvgIcon-root': { fontSize: STANDARD.checkboxSize },
          }}
        />
        {opt.color && (
          <Box
            sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: opt.color, mr: 1, flexShrink: 0 }}
          />
        )}
        <ListItemText
          primary={opt.label}
          primaryTypographyProps={{
            sx: wrapItemText
              ? { fontSize: STANDARD.itemFontSize, wordBreak: 'break-word', whiteSpace: 'normal', lineHeight: 1.3 }
              : { fontSize: STANDARD.itemFontSize, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
          }}
        />
      </MenuItem>
    ));
  };

  return (
    <FormControl fullWidth={fullWidth} size="small" sx={sx}>
      <InputLabel sx={{ fontSize: STANDARD.fontSize }}>{label}</InputLabel>
      <Select
        multiple
        value={selectedValues}
        onChange={(e) => onChange(e.target.value as string[])}
        input={selectOutlinedInput}
        disabled={disabled}
        MenuProps={selectMenuProps}
        renderValue={renderValueProp || defaultRenderValue}
      >
        {renderMenuItems()}
      </Select>
    </FormControl>
  );
};

export default SelectAllMultiSelect;

export { STANDARD as FILTER_STANDARD };
